import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  static #instances = new Set();
  static #historyRecovery;
  static #historyWritesPending = false;
  static #historyGeneration = 0;
  #mutationPromise;
  #historyStale = false;
  #historyCanceledChanges = false;
  #historyInertTargets = new Map();
  #historyRefreshCleanup;
  #historyObserver = new MutationObserver(() => {
    if (this.isConnected && this.#historyStale) this.#guardHistoryCart();
  });

  // A restored document can have a correct badge but obsolete line keys and prices.
  // Keep only cart actions unavailable until server markup matches a fresh cart read.
  #onPageShow = (event) => {
    if (event.persisted) CartItemsComponent.#recoverHistoryCart();
  };

  #onPageHide = () => {
    CartItemsComponent.#historyGeneration++;
    CartItemsComponent.#historyRecovery = undefined;
    CartItemsComponent.#historyWritesPending = false;
    if (this.#historyStale) {
      this.#refreshVersion++;
      this.#historyRefreshCleanup?.();
    }
  };

  static #recoverHistoryCart = (restart = false) => {
    if (CartItemsComponent.#historyRecovery && restart !== true) return CartItemsComponent.#historyRecovery;
    if (restart === true) {
      for (const cart of CartItemsComponent.#instances) {
        if (!cart.#historyStale) continue;
        cart.#refreshVersion++;
        cart.#historyRefreshCleanup?.();
      }
    }
    const generation = ++CartItemsComponent.#historyGeneration;
    const carts = [...CartItemsComponent.#instances].filter((cart) => cart.isConnected);
    for (const cart of carts) {
      cart.#historyStale = true;
      cart.#historyCanceledChanges ||= cart.#queuedChanges.size > 0;
      cart.#cancelQueuedChanges();
      cart.#refreshVersion++;
      cart.#guardHistoryCart();
      cart.querySelector('[data-cart-history-retry]')?.remove();
    }
    CartItemsComponent.#historyWritesPending = true;
    CartItemsComponent.#historyRecovery = (async () => {
      // A sent write must settle; neither cancel it nor repeat it on restoration.
      await Promise.allSettled(carts.map((cart) => cart.#mutationPromise));
      if (generation !== CartItemsComponent.#historyGeneration) return;
      CartItemsComponent.#historyWritesPending = false;
      await Promise.all(carts.filter((cart) => cart.isConnected).map((cart) =>
        cart.#handleCartUpdate(new CartUpdateEvent(undefined, cart.sectionId, {
          source: 'cart-items-reconcile',
        }))
      ));
    })().finally(() => {
      if (generation !== CartItemsComponent.#historyGeneration) return;
      CartItemsComponent.#historyWritesPending = false;
      CartItemsComponent.#historyRecovery = undefined;
    });
    return CartItemsComponent.#historyRecovery;
  };

  #guardHistoryCart() {
    this.setAttribute('data-cart-history-pending', '');
    for (const target of this.querySelectorAll('.cart-form, .cart__ctas, cart-discount-component, cart-note')) {
      if (!this.#historyInertTargets.has(target)) this.#historyInertTargets.set(target, target.inert);
      if (!target.inert) target.inert = true;
    }
    this.#historyObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert'] });
  }

  #releaseHistoryCart() {
    this.#historyStale = false;
    this.#historyObserver.disconnect();
    this.removeAttribute('data-cart-history-pending');
    for (const [target, inert] of this.#historyInertTargets) target.inert = inert;
    this.#historyInertTargets.clear();
    this.querySelector('[data-cart-history-retry]')?.remove();
  }

  #showHistoryRetry() {
    if (!this.#historyStale || this.querySelector('[data-cart-history-retry]')) return;
    this.#guardHistoryCart();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button';
    button.setAttribute('data-cart-history-retry', '');
    button.textContent = Theme.translations.search_retry;
    button.addEventListener('click', CartItemsComponent.#recoverHistoryCart);
    this.append(button);
  }

  #debouncedOnChange = debounce(() => this.#drainQuantityChanges(), 300);
  #queuedChanges = new Map();

  #mutationPending = false;
  #disabledControls = new Map();

  #updateVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    CartItemsComponent.#instances.add(this);
    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('pagehide', this.#onPageHide);

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    if (this.#historyStale || CartItemsComponent.#historyRecovery) CartItemsComponent.#recoverHistoryCart(true);
  }

  disconnectedCallback() {
    this.#cancelQueuedChanges();
    CartItemsComponent.#instances.delete(this);
    window.removeEventListener('pageshow', this.#onPageShow);
    window.removeEventListener('pagehide', this.#onPageHide);
    const wasHistoryStale = this.#historyStale;
    this.#releaseHistoryCart();
    this.#historyStale = wasHistoryStale;
    super.disconnectedCallback();
    this.#refreshVersion++;
    this.#updateVersion++;
    this.#historyRefreshCleanup?.();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
  }

  #queueQuantityChange = (event) => {
    if (this.#historyStale || this.#mutationPending || !(event.target instanceof Node) || !this.contains(event.target)) return;
    const key = this.refs.cartItemRows?.[event.detail.cartLine - 1]?.dataset.key;
    if (!key) return;
    const quantity = event.detail.quantity;
    this.#queuedChanges.set(key, { key, quantity, action: quantity === 0 ? 'clear' : 'change' });
    this.#debouncedOnChange();
  };

  // Coalesce only the same line. Different line intentions must survive the debounce
  // and each server-rendered response, then resolve by key rather than stale index.
  #drainQuantityChanges = () => {
    if (this.#historyStale || this.#mutationPending || !this.isConnected) return Promise.resolve();
    const next = this.#queuedChanges.entries().next().value;
    if (!next) return Promise.resolve();
    const [key, config] = next;
    this.#queuedChanges.delete(key);
    const line = this.refs.cartItemRows.findIndex((row) => row.dataset.key === key) + 1;
    if (!line) {
      this.#cancelQueuedChanges();
      this.#handleCartError(0, { errors: Theme.translations.cart_update_error });
      return Promise.resolve();
    }
    const row = this.refs.cartItemRows[line - 1];
    row?.querySelector('text-component')?.shimmer();
    return this.updateQuantity({ ...config, line });
  };

  #cancelQueuedChanges() {
    for (const key of this.#queuedChanges.keys()) {
      const line = this.refs.cartItemRows.findIndex((row) => row.dataset.key === key) + 1;
      const input = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
      if (input) input.value = input.defaultValue;
    }
    this.#queuedChanges.clear();
    this.#debouncedOnChange.cancel();
  }

  /** @param {number} line - The current line item index. */
  onLineItemRemove(line) {
    if (this.#historyStale || this.#mutationPending) return Promise.resolve();
    const key = this.refs.cartItemRows?.[line - 1]?.dataset.key;
    if (!key) return Promise.resolve();
    this.#queuedChanges.set(key, { key, quantity: 0, action: 'clear' });
    this.#debouncedOnChange.cancel();
    return this.#drainQuantityChanges();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The stable cart line key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.#historyStale || this.#mutationPending) return Promise.resolve();
    const key = config.key || this.refs.cartItemRows?.[config.line - 1]?.dataset.key;
    if (!key) {
      this.#handleCartError(config.line, { errors: Theme.translations.cart_update_error });
      return Promise.resolve();
    }
    this.#mutationPending = true;
    this.#refreshVersion++;
    this.#debouncedOnChange.cancel();
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    const version = ++this.#updateVersion;
    const isCurrent = () => version === this.#updateVersion && this.isConnected;
    let successful = false;
    let stale = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return this.#mutationPromise = fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig('json', { body }), signal: controller.signal })
      .then(async (response) => {
        const parsedResponse = await response.json();
        if (controller.signal.aborted) throw new Error('Cart update timed out');
        if (!response.ok && !parsedResponse.errors) throw new Error(`Cart update failed: HTTP ${response.status}`);
        return parsedResponse;
      })
      .then(async (parsedResponseText) => {
        if (!isCurrent()) {
          stale = true;
          return;
        }
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        if (typeof sectionHTML !== 'string' || !sectionHTML.trim()) {
          throw new Error('Cart response is missing section markup');
        }
        const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

        const newSection = newSectionHTML.getElementById(buildSectionSelector(this.sectionId));
        const newCart = newSection?.querySelector('cart-items-component');
        const countText = newCart?.querySelector('[ref="cartItemCount"]')?.textContent?.trim() ?? '';
        const renderedCount = /^\d+$/.test(countText) ? Number(countText) : undefined;
        const payloadCount = parsedResponseText.item_count;
        const newCartItemCount = Number.isSafeInteger(payloadCount) && payloadCount >= 0 ? payloadCount : renderedCount;
        if (
          !newCart || newCart.dataset.sectionId !== this.sectionId ||
          !Number.isSafeInteger(newCartItemCount) || newCartItemCount < 0 ||
          (renderedCount !== undefined && renderedCount !== newCartItemCount) ||
          (newCartItemCount > 0 && renderedCount === undefined) ||
          (newCartItemCount === 0 && !newCart.querySelector('.cart-items__empty-button'))
        ) {
          throw new Error('Cart response contains invalid cart markup or count');
        }
        if (!this.#matchesCartSnapshot(sectionHTML, parsedResponseText)) {
          throw new Error('Cart section does not match the confirmed line quantities');
        }

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        await morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });

        successful = isCurrent();
        if (successful) this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(() => {
        if (isCurrent()) {
          this.#handleCartError(line, { errors: Theme.translations.cart_update_error });
        } else {
          stale = true;
        }
      })
      .finally(() => {
        clearTimeout(timeout);
        // Request ownership is separate from display freshness: an obsolete result
        // must still release the controls it disabled, without rendering its data.
        resetShimmer(this);
        this.#enableCartItems();
        this.#mutationPending = false;
        if (!successful) this.#cancelQueuedChanges();
        if (this.isConnected) this.#updateCartQuantitySelectorButtonStates();
        if (stale) {
          document.dispatchEvent(new CartUpdateEvent(undefined, this.sectionId, {
            source: 'cart-items-reconcile',
          }));
        } else if (successful) {
          this.#drainQuantityChanges();
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;
    this.#updateCartQuantitySelectorButtonStates();

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    const message = typeof parsedResponseText.errors === 'string'
      ? parsedResponseText.errors : Theme.translations.cart_update_error;
    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      let error = this.querySelector('[data-cart-request-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-cart-request-error', '');
        error.setAttribute('role', 'alert');
        this.append(error);
      }
      error.textContent = message;
    }
  };

  #refreshVersion = 0;

  /** @param {Array<{key: string, quantity: number}>} items @returns {string} */
  #cartSignature(items) {
    const lines = items.map(({ key, quantity }) => {
      if (typeof key !== 'string' || !key || !Number.isSafeInteger(quantity) || quantity <= 0) {
        throw new Error('Cart snapshot has invalid line data');
      }
      return [key, quantity];
    });
    if (new Set(lines.map(([key]) => key)).size !== lines.length) throw new Error('Cart snapshot has duplicate lines');
    return JSON.stringify(lines.sort(([first], [second]) => String(first).localeCompare(String(second))));
  }

  /** @param {string} html @param {{item_count?: number, items?: Array<{key: string, quantity: number}>}} [resource] */
  #matchesCartSnapshot(html, resource) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const section = parsed.getElementById(buildSectionSelector(this.sectionId));
    const cart = section?.querySelector('cart-items-component');
    if (!cart || cart.dataset.sectionId !== this.sectionId) throw new Error('Cart markup has no matching component');
    const countText = cart.querySelector('[ref="cartItemCount"]')?.textContent?.trim() ?? '';
    let count = /^\d+$/.test(countText) ? Number(countText) : undefined;
    const rows = Array.from(cart.querySelectorAll('[ref="cartItemRows[]"]'));
    if (rows.length) {
      if (!Number.isSafeInteger(count) || count <= 0) throw new Error('Cart markup has no valid count');
    } else {
      if ((countText && count !== 0) || !cart.querySelector('.cart-items__empty-button')) {
        throw new Error('Cart markup has no valid empty state');
      }
      count = 0;
    }
    const signature = this.#cartSignature(rows.map((row) => ({
      key: /** @type {HTMLElement} */ (row).dataset.key ?? '',
      quantity: Number(row.querySelector('[ref="quantityInput"]')?.getAttribute('value')),
    })));
    const confirmedCount = resource?.item_count;
    return (!Number.isSafeInteger(confirmedCount) || confirmedCount < 0 || count === confirmedCount) &&
      (!Array.isArray(resource?.items) || signature === this.#cartSignature(resource.items));
  }

  /**
   * Handles the cart update.
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event.target === this) return;
    const canceledQueuedChanges = this.#queuedChanges.size > 0;
    this.#cancelQueuedChanges();
    const version = ++this.#refreshVersion;
    this.#updateVersion++;
    this.#historyRefreshCleanup?.();
    const reconcile = event.detail.data?.source === 'cart-items-reconcile' ||
      (this.#historyStale && !Array.isArray(event.detail.resource?.items)) ||
      (event instanceof CartAddEvent && !Array.isArray(event.detail.resource?.items));
    const isCurrent = () => version === this.#refreshVersion && this.isConnected;
    let expired = false;
    const controller = new AbortController();
    /** @type {number | undefined} */
    let timeout;
    let historyCleanup;

    try {
      let cartItemsHtml = event instanceof DiscountUpdateEvent || reconcile
        ? undefined : event.detail.data?.sections?.[this.sectionId];
      let resource = event.detail.resource;
      let needsRefresh = !cartItemsHtml;
      if (cartItemsHtml) {
        try {
          needsRefresh = !this.#matchesCartSnapshot(cartItemsHtml, resource);
        } catch (error) {
          needsRefresh = true;
        }
      }

      if (needsRefresh) {
        const canceled = new Promise((resolve, reject) => {
          if (!this.#historyStale) return;
          historyCleanup = () => {
            expired = true;
            controller.abort();
            clearTimeout(timeout);
            reject(new Error('History cart refresh was superseded'));
          };
          this.#historyRefreshCleanup = historyCleanup;
        });
        cartItemsHtml = await Promise.race([
          canceled,
          (async () => {
            if (event instanceof DiscountUpdateEvent || reconcile) {
              const response = await fetch(`${Theme.routes.cart_url}.js`, { signal: controller.signal, cache: 'no-store' });
              if (!response.ok) throw new Error('Cart reconciliation failed');
              resource = await response.json();
              if (!isCurrent() || expired) return;
              if (!Array.isArray(resource?.items) || !Number.isSafeInteger(resource.item_count) || resource.item_count < 0) {
                throw new Error('Cart reconciliation returned invalid data');
              }
            }
            for (let attempt = 0; attempt < 2; attempt++) {
              const freshHtml = await sectionRenderer.getSectionHTML(this.sectionId, false);
              if (!isCurrent() || expired) return;
              if (this.#matchesCartSnapshot(freshHtml, resource)) return freshHtml;
            }
            throw new Error('Cart refresh does not match the confirmed cart');
          })(),
          new Promise((resolve, reject) => {
            timeout = setTimeout(() => {
              expired = true;
              controller.abort();
              reject(new Error('Cart refresh timed out'));
            }, 15000);
          }),
        ]);
      }
      if (!isCurrent() || expired) return;
      await morphSection(this.sectionId, cartItemsHtml);
      if (isCurrent()) {
        if (this.#historyStale && Array.isArray(resource?.items) &&
          !CartItemsComponent.#historyWritesPending && !this.#mutationPending) {
          this.#releaseHistoryCart();
          if (this.#historyCanceledChanges) {
            this.#historyCanceledChanges = false;
            this.#handleCartError(0, { errors: Theme.translations.cart_update_error });
          }
        }
        this.#updateCartQuantitySelectorButtonStates();
        if (canceledQueuedChanges) this.#handleCartError(0, { errors: Theme.translations.cart_update_error });
        if (this.#mutationPending) this.#disableCartItems();
        if (this.#historyStale) this.#guardHistoryCart();
        if (reconcile) {
          this.#updateQuantitySelectors(resource);
          this.dispatchEvent(new CartUpdateEvent(resource, this.sectionId, {
            source: 'cart-items-component',
            didError: event.detail.data?.didError === true,
            sections: { [this.sectionId]: cartItemsHtml },
          }));
        }
      }
    } catch (error) {
      if (isCurrent()) this.#handleCartError(0, { errors: Theme.translations.cart_update_error });
    } finally {
      clearTimeout(timeout);
      controller.abort();
      if (this.#historyRefreshCleanup === historyCleanup) this.#historyRefreshCleanup = undefined;
      if (isCurrent()) this.#showHistoryRetry();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    for (const control of this.querySelectorAll('button, input, select, textarea')) {
      if (!this.#disabledControls.has(control)) this.#disabledControls.set(control, control.disabled);
      control.disabled = true;
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    for (const [control, disabled] of this.#disabledControls) control.disabled = disabled;
    this.#disabledControls.clear();
    if (this.#historyStale) this.#guardHistoryCart();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      if (selector.closest('cart-items-component[aria-busy="true"]')) continue;
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
